// CancelPage — the customer cancellation request form.
// Plan: docs/plans/2026-09-30-customer-cancellation-request-flow.md §2, §3.
//
//   /cancel             (?for=bkos)  — step 1: help first (a call, a note), then "email me the link"
//   /cancel/r/:token                 — steps 2–4: what you hold, what would
//                                      happen, the request, the confirmation
//   /cancel/w/:token                 — withdraw a request
//
// Every request is reviewed by a person, so the page never says "cancelled":
// it shows what will NORMALLY happen (computed by the engine, never page copy)
// and that the team will confirm. One host for both brands
// (discover.thesuccessfulbookkeeper.com); the retired BKOS and PB taker hosts
// are redirected in main.tsx. The wording is the EE fallback of the CC asset.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

type Brand = 'tsb' | 'bkos';

interface Item {
  membership_id: string;
  plan_name: string;
  also_ends: string[];
  cadence: string;
  term_ends_on: string | null;
  renewal_on: string | null;
  policy: string;
  ends_on_words: string;
  refund_by_rule: boolean;
  outcome: string;
  losing: string[];
  call: 'required' | 'optional' | 'none';
  open_request: { reference: string | null; status: string } | null;
  change_plan_options: Array<{ tier_key: string; name: string }>;
}

interface PageData {
  email: string;
  brand: Brand;
  items: Item[];
  reasons: Array<{ code: string; label: string }>;
  unmatched_request: { reference: string | null; status: string } | null;
}

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 36, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.1, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 24px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const btn: CSSProperties = { background: '#F05A28', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer' };
const ghost: CSSProperties = { background: 'transparent', color: '#0A0A2E', border: '1px solid #d6d3cc', borderRadius: 10, padding: '11px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid #d6d3cc', background: '#fff', fontFamily: 'inherit', color: '#0A0A2E' };
const small: CSSProperties = { fontSize: 14, color: '#6b6b80', lineHeight: 1.5 };

function Shell({ brand, children }: { brand: Brand; children: ReactNode }) {
  return (
    <TakerShell footerLabel={brand === 'bkos' ? 'BKOS by The Successful Bookkeeper' : 'The Successful Bookkeeper'}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '44px 16px 80px' }}>{children}</div>
    </TakerShell>
  );
}

function ErrorLine({ text }: { text: string }) {
  return text ? <p role="alert" style={{ color: '#B42318', fontSize: 15, margin: '12px 0 0' }}>{text}</p> : null;
}

async function readError(r: Response): Promise<string> {
  try { const d = await r.json(); return d?.error ?? `Something went wrong (${r.status}).`; } catch { return `Something went wrong (${r.status}).`; }
}

// ── Step 1: the "save" conversation ───────────────────────────────────────
// Michael, 25 Oct 2026 (mct_f7f0fbe8-ca6): once someone has decided to cancel
// we are done; while it is still a question, a call or a quick fix can keep
// them. So the first screen opens as a question and offers help first: a call
// with the exit-call host, a note to the team, what other members say. The
// cancel form keeps its one field on the same screen, nothing added in front
// of it (consumer law in CA, US, AU: help first, never a wall). Everything
// offered is computed by the engine (/intro): no host, no quote, no booking
// link is typed into this file.

const BRAND_MARKS = [
  { key: 'tsb', alt: 'The Successful Bookkeeper', src: 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/tsb/logo-light.svg', height: 40 },
  { key: 'bkos', alt: 'BKOS', src: 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/bkos/logo-light.svg', height: 26 },
  { key: 'pb', alt: 'Pure Bookkeeping', src: 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/pb/logo-light.svg', height: 46 },
];

/** One page for every home a member may have come from — a legacy Pure
 *  Bookkeeping member needs to see their mark to know this is their page
 *  (Michael, 26 Oct 2026). */
function BrandStrip() {
  return (
    <div style={{ background: '#fff', borderBottom: '1px solid #ebe7e0', padding: '14px 16px 12px', textAlign: 'center' }}>
      {/* Three marks on one row at every width: a phone scales them down
          rather than wrapping the third onto its own line. */}
      <style>{`
        .cancel-brands { display: flex; justify-content: center; align-items: center; gap: 34px; }
        .cancel-brands img { height: var(--h); width: auto; max-width: 190px; }
        @media (max-width: 520px) { .cancel-brands { gap: 18px; } .cancel-brands img { height: calc(var(--h) * 0.7); } }
      `}</style>
      <p style={{ fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', color: '#6b6b80', margin: '0 0 10px' }}>One page for members of</p>
      <div className="cancel-brands">
        {BRAND_MARKS.map((m) => <img key={m.key} src={m.src} alt={m.alt} style={{ '--h': `${m.height}px` } as CSSProperties} />)}
      </div>
    </div>
  );
}

interface Intro {
  call: { url: string; host_name: string; host_first_name: string; host_role: string | null; photo_url: string | null; minutes: number } | null;
  quotes: Array<{ quote: string; who: string | null; company: string | null }>;
}

const eyebrow: CSSProperties = { fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', color: '#F05A28', fontWeight: 700, margin: '0 0 6px' };
const h2: CSSProperties = { fontSize: 19, fontWeight: 700, margin: '0 0 6px', color: '#0A0A2E' };
const body: CSSProperties = { margin: '0 0 10px', color: '#44445e', fontSize: 15.5, lineHeight: 1.5 };
const fieldLabel: CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 8 };

export function CancelStartPage() {
  const [params] = useSearchParams();
  const brand: Brand = params.get('for') === 'bkos' ? 'bkos' : 'tsb';
  const [intro, setIntro] = useState<Intro | null>(null);

  useEffect(() => {
    // Offers are a bonus, never a gate: a failed fetch hides the cards and the
    // cancel form still works.
    fetch(`${API}/api/public/cancel/intro?brand=${brand}`)
      .then(async (r) => { if (r.ok) setIntro(await r.json()); })
      .catch(() => {});
  }, [brand]);

  const planWords = brand === 'bkos' ? 'your BKOS plan' : 'your plan or membership';

  return (
    <TakerShell footerLabel={brand === 'bkos' ? 'BKOS by The Successful Bookkeeper' : 'The Successful Bookkeeper'}>
      <BrandStrip />
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '44px 16px 80px' }}>
        <h1 style={h1}>Thinking about cancelling{brand === 'bkos' ? ' your BKOS plan' : ''}?</h1>
        <p style={lead}>
          Before anything changes, let's see if we can help. And if you have decided, cancelling takes two minutes and starts with your email, below.
        </p>

        {intro?.call && <CallCard call={intro.call} />}
        <HelpNoteCard brand={brand} />
        {intro && intro.quotes.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, margin: '6px 0 18px' }}>
            {intro.quotes.map((q, i) => (
              <blockquote key={i} style={{ background: '#FFF4EF', borderRadius: 12, padding: '14px 16px', fontSize: 14.5, color: '#0A0A2E', lineHeight: 1.5, margin: 0 }}>
                &ldquo;{q.quote}&rdquo;
                <cite style={{ display: 'block', fontStyle: 'normal', fontSize: 13, color: '#6b6b80', marginTop: 8 }}>
                  {q.who ? `${q.who}${q.company ? `, ${q.company}` : ''}` : 'A member'}
                </cite>
              </blockquote>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#6b6b80', fontSize: 13, letterSpacing: '.06em', textTransform: 'uppercase', margin: '26px 0 16px' }}>
          <span style={{ flex: 1, height: 1, background: '#d6d3cc' }} />If you have decided<span style={{ flex: 1, height: 1, background: '#d6d3cc' }} />
        </div>

        <CancelEmailCard brand={brand} planWords={planWords} />
      </div>
    </TakerShell>
  );
}

function CallCard({ call }: { call: NonNullable<Intro['call']> }) {
  return (
    <div style={card}>
      <p style={eyebrow}>Talk it through first</p>
      <h2 style={h2}>A short call with {call.host_first_name}</h2>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', margin: '10px 0 12px' }}>
        {call.photo_url
          ? <img src={call.photo_url} alt={call.host_name} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flex: 'none' }} />
          : <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg,#f6c7b2,#F05A28)', flex: 'none' }} />}
        <div style={{ fontSize: 14, color: '#44445e' }}>
          <strong style={{ color: '#0A0A2E', display: 'block', fontSize: 15 }}>{call.host_name}</strong>
          {call.host_role ?? 'The Successful Bookkeeper team'}
        </div>
      </div>
      <p style={body}>
        {call.minutes} minutes, no pitch. If the price, the time it takes, or where to start is the problem, {call.host_first_name} has usually seen it before and can often sort it on the call.
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
        <a href={call.url} style={{ ...btn, display: 'inline-block', textDecoration: 'none' }}>Book a call with {call.host_first_name}</a>
        <span style={small}>Booking a call changes nothing about your plan or billing.</span>
      </div>
    </div>
  );
}

function HelpNoteCard({ brand }: { brand: Brand }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/public/cancel/help`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, brand, message }),
      });
      if (!r.ok) { setErr(await readError(r)); return; }
      setSent(true);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div style={card}>
        <p style={eyebrow}>Sent to the team</p>
        <h2 style={h2}>Thank you. A real person will reply to {email}.</h2>
        <p style={{ ...small, margin: 0 }}>Usually within one business day. Nothing has changed on your plan.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} style={card}>
      <p style={eyebrow}>Or tell us what is getting in the way</p>
      <h2 style={h2}>Something we can fix?</h2>
      <p style={body}>
        A real person reads every note and replies within one business day. Price, a billing date, a feature that is not working, a plan that no longer fits: say so, and we will see what we can do.
      </p>
      <label htmlFor="help-email" style={fieldLabel}>Your email address</label>
      <input id="help-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} style={input} placeholder="you@yourfirm.com" />
      <label htmlFor="help-message" style={{ ...fieldLabel, marginTop: 12 }}>What is getting in the way?</label>
      <textarea id="help-message" required value={message} onChange={(e) => setMessage(e.target.value)} rows={3} style={{ ...input, resize: 'vertical' }}
        placeholder="For example: the monthly price is more than I can manage right now." />
      <div style={{ marginTop: 14 }}>
        <button type="submit" disabled={busy || !email || message.trim().length < 3} style={{ ...ghost, opacity: busy || !email || message.trim().length < 3 ? 0.6 : 1 }}>
          {busy ? 'Sending…' : 'Send to the team'}
        </button>
      </div>
      <ErrorLine text={err} />
    </form>
  );
}

function CancelEmailCard({ brand, planWords }: { brand: Brand; planWords: string }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/public/cancel/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, brand }),
      });
      if (!r.ok) { setErr(await readError(r)); return; }
      setSent(true);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div style={card}>
        <p style={{ ...lead, margin: '0 0 10px' }}><strong>Check your inbox.</strong></p>
        <p style={small}>
          We have sent a link to <strong>{email}</strong>. It works for 24 hours and takes you to the next step.
          If you do not see it in a few minutes, check your spam folder, or make sure this is the address you signed up with.
        </p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} style={card}>
      <h2 style={h2}>Cancel {planWords}</h2>
      <p style={body}>
        Enter the email you signed up with and we will send you a secure link. The next page shows exactly what would happen and when; nothing is cancelled until you confirm there and our team confirms with you.
      </p>
      <label htmlFor="cancel-email" style={fieldLabel}>Your email address</label>
      <input id="cancel-email" type="email" required autoComplete="email" value={email}
        onChange={(e) => setEmail(e.target.value)} style={input} placeholder="you@yourfirm.com" />
      <div style={{ marginTop: 16 }}>
        <button type="submit" disabled={busy || !email} style={{ ...btn, opacity: busy || !email ? 0.6 : 1 }}>
          {busy ? 'Sending…' : 'Email me the link'}
        </button>
      </div>
      <ErrorLine text={err} />
      <p style={{ ...small, marginTop: 12 }}>
        You can withdraw a request any time before your access ends. Billing through a different email address, or asking on behalf of your firm? Write to support@thesuccessfulbookkeeper.com, and we will help.
      </p>
    </form>
  );
}

// ── Steps 2–4 ─────────────────────────────────────────────────────────────────

type Done = { reference: string | null; already: boolean; call: string; booking_url: string | null; kind: 'cancel' | 'plan_change' };

export function CancelRequestPage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<PageData | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid' | 'error'>('loading');
  const [chosen, setChosen] = useState<string | null>(null); // membership_id or 'unmatched'
  const [mode, setMode] = useState<'cancel' | 'change'>('cancel');
  const [toTier, setToTier] = useState('');
  const [reasons, setReasons] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<Done | null>(null);

  useEffect(() => {
    fetch(`${API}/api/public/cancel/request/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 401) { setStatus('invalid'); return; }
        if (!r.ok) { setStatus('error'); return; }
        const d = (await r.json()) as PageData;
        setData(d);
        const open = d.items.filter((i) => !i.open_request);
        setChosen(d.items.length === 0 ? 'unmatched' : open.length === 1 ? open[0].membership_id : null);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [token]);

  const brand: Brand = data?.brand ?? 'tsb';
  const item = data?.items.find((i) => i.membership_id === chosen) ?? null;

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const path = mode === 'change' ? 'change-plan' : 'submit';
      const body = mode === 'change'
        ? { membership_id: chosen, to_tier_key: toTier, reason, name }
        : { membership_id: chosen === 'unmatched' ? null : chosen, reason_codes: reasons, reason, name, confirm };
      const r = await fetch(`${API}/api/public/cancel/request/${encodeURIComponent(token)}/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) { setErr(await readError(r)); return; }
      const d = await r.json();
      setDone({ reference: d.reference ?? null, already: !!d.already, call: d.call ?? 'none', booking_url: d.booking_url ?? null, kind: mode === 'change' ? 'plan_change' : 'cancel' });
      window.scrollTo(0, 0);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally { setBusy(false); }
  };

  if (status === 'loading') return <Shell brand="tsb"><p style={lead}>Loading…</p></Shell>;
  if (status === 'invalid') {
    return (
      <Shell brand="tsb">
        <h1 style={h1}>This link has expired</h1>
        <p style={lead}>For your security, cancellation links work for 24 hours. <a href="/cancel">Ask for a new link</a>.</p>
      </Shell>
    );
  }
  if (status === 'error' || !data) {
    return <Shell brand="tsb"><h1 style={h1}>Something went wrong</h1><p style={lead}>Please try the link again in a moment.</p></Shell>;
  }

  if (done) {
    return (
      <Shell brand={brand}>
        <h1 style={h1}>{done.kind === 'plan_change' ? 'Plan change requested' : done.already ? 'We already have your request' : 'Request received'}</h1>
        <div style={card}>
          {done.reference && <p style={{ ...lead, margin: '0 0 10px' }}>Your reference is <strong>{done.reference}</strong>.</p>}
          <p style={small}>
            {done.kind === 'plan_change'
              ? 'Nothing changes yet. A member of our team will confirm the details with you, usually within one business day.'
              : 'Our team reviews every request, usually within one business day, and will email you to confirm the date. We have sent you an email with the details and a link to withdraw your request if you change your mind.'}
          </p>
        </div>
        {done.kind === 'cancel' && done.call !== 'none' && done.booking_url && (
          <div style={card}>
            <p style={{ ...lead, margin: '0 0 12px' }}>
              <strong>Next step: {done.call === 'required' ? 'a short call with our team' : 'a short call, if you would like one'}.</strong>
            </p>
            <p style={small}>It helps us close things off properly with you. It never costs you an extra payment: if your next payment comes first, our team confirms your cancellation before it is taken.</p>
            <a href={done.booking_url} style={{ ...btn, display: 'inline-block', textDecoration: 'none', marginTop: 10 }}>Book a short call</a>
          </div>
        )}
      </Shell>
    );
  }

  const openItems = data.items.filter((i) => !i.open_request);
  const pendingItems = data.items.filter((i) => i.open_request);
  const canSubmit = mode === 'change'
    ? !!chosen && !!toTier
    : !!chosen && confirm && (chosen !== 'unmatched' || !data.unmatched_request);

  return (
    <Shell brand={brand}>
      <h1 style={h1}>Your cancellation request</h1>
      <p style={lead}>For <strong>{data.email}</strong>. Nothing is cancelled until our team confirms it with you.</p>

      {pendingItems.map((i) => (
        <div key={i.membership_id} style={{ ...card, background: '#FFF9F6' }}>
          <strong>{i.plan_name}</strong>: we already have your request{i.open_request?.reference ? ` (${i.open_request.reference})` : ''}, and it is {i.open_request?.status === 'approved' ? 'confirmed' : 'with our team'}.
          The email we sent you has a link to withdraw it.
        </div>
      ))}

      {data.items.length === 0 ? (
        <div style={card}>
          <p style={{ ...lead, margin: '0 0 10px' }}><strong>We could not match a plan to this email address.</strong></p>
          <p style={small}>
            That can happen if you bought through an older checkout or under a different address. You can still send your
            request to our team, and a real person will look into it and reply to you.
          </p>
          {data.unmatched_request && <p style={small}>We already have a request from you ({data.unmatched_request.reference}); our team is on it.</p>}
        </div>
      ) : openItems.length > 1 ? (
        <div style={card}>
          <p style={{ fontWeight: 700, margin: '0 0 10px' }}>Which would you like to cancel?</p>
          {openItems.map((i) => (
            <label key={i.membership_id} style={{ display: 'block', padding: '10px 0', borderTop: '1px solid #eee', cursor: 'pointer' }}>
              <input type="radio" name="item" checked={chosen === i.membership_id} onChange={() => setChosen(i.membership_id)} />{' '}
              <strong>{i.plan_name}</strong>
              {i.also_ends.length > 0 && <span style={small}> (your {i.also_ends.join(' and ')} ends with it)</span>}
            </label>
          ))}
        </div>
      ) : null}

      {item && (
        <div style={card}>
          <p style={{ fontWeight: 700, margin: '0 0 6px' }}>{item.plan_name}{item.cadence === 'annual' ? ', billed yearly' : item.cadence === 'monthly' ? ', billed monthly' : ''}</p>
          {item.also_ends.length > 0 && <p style={small}>Your {item.plan_name} and your {item.also_ends.join(' and ')} end together.</p>}
          <div style={{ background: '#f8f6f3', borderRadius: 10, padding: '12px 14px', margin: '12px 0' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 700 }}>What will normally happen</p>
            <p style={{ ...small, margin: 0, color: '#0A0A2E' }}>{item.outcome}</p>
            {item.losing.length > 0 && <p style={{ ...small, margin: '8px 0 0' }}>When access ends, these end with it: {item.losing.join('; ')}.</p>}
          </div>
          <p style={small}>Our team confirms every request and will email you the confirmed date.</p>
          {item.change_plan_options.length > 0 && (
            <p style={{ ...small, marginTop: 10 }}>
              Would a different plan suit you better?{' '}
              <button type="button" onClick={() => setMode(mode === 'change' ? 'cancel' : 'change')} style={{ background: 'none', border: 'none', color: '#F05A28', fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 14 }}>
                {mode === 'change' ? 'No, I want to cancel' : 'Ask to change plan instead'}
              </button>
            </p>
          )}
        </div>
      )}

      {(chosen && (chosen !== 'unmatched' || !data.unmatched_request)) && (
        <div style={card}>
          {mode === 'change' && item ? (
            <>
              <p style={{ fontWeight: 700, margin: '0 0 10px' }}>Which plan would you like instead?</p>
              {item.change_plan_options.map((o) => (
                <label key={o.tier_key} style={{ display: 'block', padding: '6px 0', cursor: 'pointer' }}>
                  <input type="radio" name="to" checked={toTier === o.tier_key} onChange={() => setToTier(o.tier_key)} /> {o.name}
                </label>
              ))}
            </>
          ) : (
            <>
              <p style={{ fontWeight: 700, margin: '0 0 4px' }}>Would you tell us why? <span style={{ fontWeight: 400, color: '#6b6b80' }}>(optional)</span></p>
              <p style={{ ...small, margin: '0 0 8px' }}>It helps us do better. Tick any that apply.</p>
              {data.reasons.map((r) => (
                <label key={r.code} style={{ display: 'block', padding: '5px 0', cursor: 'pointer', fontSize: 15 }}>
                  <input type="checkbox" checked={reasons.includes(r.code)}
                    onChange={(e) => setReasons(e.target.checked ? [...reasons, r.code] : reasons.filter((c) => c !== r.code))} /> {r.label}
                </label>
              ))}
            </>
          )}
          <label htmlFor="cancel-reason" style={{ display: 'block', fontWeight: 600, margin: '14px 0 6px' }}>Anything you would like us to know? <span style={{ fontWeight: 400, color: '#6b6b80' }}>(optional)</span></label>
          <textarea id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} style={{ ...input, resize: 'vertical' }} />
          <label htmlFor="cancel-name" style={{ display: 'block', fontWeight: 600, margin: '14px 0 6px' }}>Your name <span style={{ fontWeight: 400, color: '#6b6b80' }}>(optional)</span></label>
          <input id="cancel-name" value={name} onChange={(e) => setName(e.target.value)} style={input} autoComplete="name" />
          {mode === 'cancel' && (
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, cursor: 'pointer', fontSize: 15 }}>
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} style={{ marginTop: 4 }} />
              <span>{chosen === 'unmatched'
                ? 'Yes, please send my cancellation request to the team.'
                : 'Yes, I would like to cancel, and I understand the dates above are what normally happens and will be confirmed by the team.'}</span>
            </label>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <button type="button" disabled={busy || !canSubmit} onClick={submit} style={{ ...btn, opacity: busy || !canSubmit ? 0.6 : 1 }}>
              {busy ? 'Sending…' : mode === 'change' ? 'Request this plan' : chosen === 'unmatched' ? 'Send my request to the team' : 'Send my cancellation request'}
            </button>
          </div>
          <ErrorLine text={err} />
        </div>
      )}
      {openItems.length > 1 && !chosen && <p style={small}>Choose a plan above to see exactly what would happen.</p>}
    </Shell>
  );
}

// ── Withdraw ──────────────────────────────────────────────────────────────────

export function CancelWithdrawPage() {
  const { token = '' } = useParams();
  const [page, setPage] = useState<{ reference: string | null; status: string; plan_name: string | null; ends_on_words: string; can_withdraw: boolean; brand: Brand } | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid' | 'done'>('loading');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch(`${API}/api/public/cancel/withdraw/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) { setStatus('invalid'); return; }
        setPage(await r.json()); setStatus('ready');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const withdraw = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/public/cancel/withdraw/${encodeURIComponent(token)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!r.ok) { setErr(await readError(r)); return; }
      setStatus('done');
    } catch { setErr('We could not reach our system. Please try again in a moment.'); } finally { setBusy(false); }
  };

  const brand = page?.brand ?? 'tsb';
  if (status === 'loading') return <Shell brand="tsb"><p style={lead}>Loading…</p></Shell>;
  if (status === 'invalid' || !page) {
    return <Shell brand="tsb"><h1 style={h1}>We could not find that request</h1><p style={lead}>Please reply to any of our emails and we will help.</p></Shell>;
  }
  if (status === 'done') {
    return <Shell brand={brand}><h1 style={h1}>Request withdrawn</h1><p style={lead}>Nothing changes: your {page.plan_name ?? 'plan'} carries on as before. We are glad you are staying.</p></Shell>;
  }
  return (
    <Shell brand={brand}>
      <h1 style={h1}>Withdraw your cancellation request</h1>
      <div style={card}>
        <p style={{ ...lead, margin: '0 0 10px' }}>
          {page.plan_name ?? 'Your plan'}{page.reference ? `, reference ${page.reference}` : ''}.
          {page.ends_on_words ? ` Access is set to end on ${page.ends_on_words}.` : ''}
        </p>
        {page.can_withdraw ? (
          <>
            <p style={small}>Withdrawing keeps everything exactly as it is, and billing carries on as normal.</p>
            <button type="button" disabled={busy} onClick={withdraw} style={{ ...btn, marginTop: 10, opacity: busy ? 0.6 : 1 }}>{busy ? 'Withdrawing…' : 'Withdraw my request'}</button>
            <ErrorLine text={err} />
          </>
        ) : (
          <p style={small}>This request can no longer be withdrawn here{page.status === 'withdrawn' ? ' because it was already withdrawn' : ''}. Please reply to any of our emails and we will help.</p>
        )}
      </div>
      <button type="button" style={ghost} onClick={() => { window.location.href = 'https://www.thesuccessfulbookkeeper.com'; }}>Back to our website</button>
    </Shell>
  );
}
